import type { GridColumnVisibilityModel } from '@mui/x-data-grid-pro';
import type { AccountPerformanceRow, Sums } from 'src/context/api/campaignLeaks/api';
import { compareColorPct } from 'src/components/data-grid/data-grid-performance-split-business-overview';
import { Accessors } from '../shared/grid-filtering';
import { DASH, formatConversions, formatInteger, formatMoney, formatRatio } from '../shared/format';
import type { CampaignLeaksT } from '../shared/i18n';

export type { Sums };

export const ratio = (num: number, den: number): number | null => (den ? num / den : null);

export const derived = (s: Sums) => ({
  paid_cpc: ratio(s.ad_cost, s.ad_clicks),
  paid_cpa: ratio(s.ad_cost, s.ad_conversions),
  paid_roas: ratio(s.ad_conversions_value, s.ad_cost),
});

// Share always computed on the account total, even when the Brand filter is active (decision 1).
export const share = (value: number, accountTotal: number): number | null => ratio(value, accountTotal);

export const isLeadTemplate = (template?: string | null) => template?.trim().toLowerCase() === 'lead';

export const ZERO: Sums = { ad_cost: 0, ad_clicks: 0, ad_impressions: 0, ad_conversions: 0, ad_conversions_value: 0 };

export const sumAll = (list: Sums[]): Sums =>
  list.reduce(
    (acc, s) => ({
      ad_cost: acc.ad_cost + (s.ad_cost ?? 0),
      ad_clicks: acc.ad_clicks + (s.ad_clicks ?? 0),
      ad_impressions: acc.ad_impressions + (s.ad_impressions ?? 0),
      ad_conversions: acc.ad_conversions + (s.ad_conversions ?? 0),
      ad_conversions_value: acc.ad_conversions_value + (s.ad_conversions_value ?? 0),
    }),
    { ...ZERO }
  );

// ---------------------------------------------------------------- metrics

export type MetricKey =
  | 'ad_cost'
  | 'ad_clicks'
  | 'ad_impressions'
  | 'paid_cpc'
  | 'paid_revenue'
  | 'paid_roas'
  | 'paid_conv'
  | 'paid_cpa';

export type MetricKind = 'currency' | 'number' | 'conversions' | 'ratio';

export type MetricDef = {
  key: MetricKey;
  /** Translation key of the column header (dashboard.campaignLeaks.columns.*). */
  labelKey: string;
  kind: MetricKind;
  /** Additive metrics get a bar and a share (%). */
  additive: boolean;
  /** A rise is bad (red): Paid CPC and Paid CPA. */
  inverted?: boolean;
  value: (s: Sums) => number | null;
};

export const METRICS: Record<MetricKey, MetricDef> = {
  ad_cost: { key: 'ad_cost', labelKey: 'columns.ad_cost', kind: 'currency', additive: true, value: (s) => s.ad_cost },
  ad_clicks: { key: 'ad_clicks', labelKey: 'columns.ad_clicks', kind: 'number', additive: true, value: (s) => s.ad_clicks },
  ad_impressions: { key: 'ad_impressions', labelKey: 'columns.ad_impressions', kind: 'number', additive: true, value: (s) => s.ad_impressions },
  paid_cpc: { key: 'paid_cpc', labelKey: 'columns.paid_cpc', kind: 'currency', additive: false, inverted: true, value: (s) => derived(s).paid_cpc },
  paid_revenue: { key: 'paid_revenue', labelKey: 'columns.paid_revenue', kind: 'currency', additive: false, value: (s) => s.ad_conversions_value },
  paid_roas: { key: 'paid_roas', labelKey: 'columns.paid_roas', kind: 'ratio', additive: false, value: (s) => derived(s).paid_roas },
  paid_conv: { key: 'paid_conv', labelKey: 'columns.paid_conv', kind: 'conversions', additive: false, value: (s) => s.ad_conversions },
  paid_cpa: { key: 'paid_cpa', labelKey: 'columns.paid_cpa', kind: 'currency', additive: false, inverted: true, value: (s) => derived(s).paid_cpa },
};

/** Metric columns of each template, in column order, with their default visibility (✓ / ○ of ticket 5). */
export const TEMPLATE_METRICS: Record<'lead' | 'ecommerce', { key: MetricKey; visible: boolean }[]> = {
  lead: [
    { key: 'ad_cost', visible: true },
    { key: 'ad_clicks', visible: true },
    { key: 'ad_impressions', visible: false },
    { key: 'paid_cpc', visible: true },
    { key: 'paid_conv', visible: true },
    { key: 'paid_cpa', visible: true },
  ],
  ecommerce: [
    { key: 'ad_cost', visible: true },
    { key: 'ad_clicks', visible: true },
    { key: 'ad_impressions', visible: false },
    { key: 'paid_cpc', visible: true },
    { key: 'paid_revenue', visible: true },
    { key: 'paid_roas', visible: true },
    { key: 'paid_conv', visible: false },
    { key: 'paid_cpa', visible: false },
  ],
};

export const templateOf = (workspaceTemplate?: string | null) => (isLeadTemplate(workspaceTemplate) ? 'lead' : 'ecommerce');

export const metricsFor = (template: 'lead' | 'ecommerce') => TEMPLATE_METRICS[template].map(({ key }) => METRICS[key]);

export const defaultColumnVisibility = (template: 'lead' | 'ecommerce'): GridColumnVisibilityModel => ({
  campaign_type: true,
  is_brand: true,
  ...Object.fromEntries(TEMPLATE_METRICS[template].map(({ key, visible }) => [key, visible])),
});

export const columnsStorageKey = (template: 'lead' | 'ecommerce') => `campaignLeaks.performance.${template}.columns`;

export const formatMetric = (kind: MetricKind, value: number | null, currency: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) return DASH;
  switch (kind) {
    case 'currency':
      return formatMoney(value, currency);
    case 'conversions':
      return formatConversions(value);
    case 'ratio':
      return formatRatio(value);
    default:
      return formatInteger(value);
  }
};

/**
 * Share of the TOTAL row: what the displayed rows (Brand filter, column filters) weigh in the account total,
 * e.g. the Brand rows' 40k of a 182k account. 100% without filter.
 */
export const totalRowShare = (metric: MetricDef, rows: { current: Sums }[], accountTotal: Sums) =>
  share(rows.reduce((sum, row) => sum + (metric.value(row.current) ?? 0), 0), metric.value(accountTotal) ?? 0);

/** Share label: rounded, "<1%" between 0 and 1%. */
export const formatShare = (value: number | null) => {
  if (value === null) return DASH;
  const pct = value * 100;
  if (pct > 0 && pct < 1) return '<1%';
  return `${Math.round(pct)}%`;
};

// ---------------------------------------------------------------- evolution

export type Evolution = { label: string; color: string };

const NEUTRAL = '#637381';

/**
 * % evolution (current − compare) / compare, or the absolute difference in the metric format.
 * "—" when the compare value is 0 or missing.
 */
export const evolution = (
  metric: MetricDef,
  current: Sums,
  compare: Sums | null | undefined,
  { percent, currency }: { percent: boolean; currency: string }
): Evolution => {
  if (!compare) return { label: DASH, color: NEUTRAL };
  const cur = metric.value(current);
  const cmp = metric.value(compare);
  if (cur === null || cmp === null || cmp === 0) return { label: DASH, color: NEUTRAL };
  const color = compareColorPct(cur, cmp, !!metric.inverted);
  const diff = cur - cmp;
  let sign = '';
  if (diff > 0) sign = '+';
  else if (diff < 0) sign = '-';
  if (percent) return { label: `${sign}${Math.round(Math.abs((diff / cmp) * 100))}%`, color };
  return { label: `${sign}${formatMetric(metric.kind, Math.abs(diff), currency)}`, color };
};

// ---------------------------------------------------------------- rows

export type BrandFilter = 'all' | 'brand' | 'non_brand';

export const BRAND_FILTERS: BrandFilter[] = ['all', 'brand', 'non_brand'];

export const brandFilterOptions = (ct: CampaignLeaksT) =>
  BRAND_FILTERS.map((value) => ({ value, label: ct(`brand.${value}`) }));

export type PerformanceRow = AccountPerformanceRow & { id: string };

export const toGridRows = (rows: AccountPerformanceRow[] | undefined): PerformanceRow[] =>
  (rows ?? []).map((row) => ({ ...row, id: `${row.campaign_type}|${row.is_brand}` }));

export const brandLabel = (ct: CampaignLeaksT, isBrand: boolean) => ct(isBrand ? 'brand.brand' : 'brand.non_brand');

export const applyBrandFilter = (rows: PerformanceRow[], filter: BrandFilter) =>
  filter === 'all' ? rows : rows.filter((row) => row.is_brand === (filter === 'brand'));

/** Values filtered and sorted on; Brand / Non brand in the user's language, as displayed. */
export const getPerformanceAccessors = (ct: CampaignLeaksT): Accessors<PerformanceRow> => ({
  campaign_type: (row) => row.campaign_type,
  is_brand: (row) => brandLabel(ct, row.is_brand),
  ...Object.fromEntries(Object.values(METRICS).map((metric) => [metric.key, (row: PerformanceRow) => metric.value(row.current)])),
});

/** KPI cards (decision 3): sums of the displayed rows, ratios recomputed from the sums. */
export const performanceKpis = (rows: PerformanceRow[]) => {
  const sums = sumAll(rows.map((row) => row.current));
  return {
    ad_cost: sums.ad_cost,
    ad_clicks: sums.ad_clicks,
    paid_conversions: sums.ad_conversions,
    paid_cpa: derived(sums).paid_cpa,
    paid_revenue: sums.ad_conversions_value,
    paid_roas: derived(sums).paid_roas,
  };
};

/** CSV: filtered rows + TOTAL; value, compared value, share and evolution of every metric of the template. */
export const performanceCsv = (
  rows: PerformanceRow[],
  total: { current: Sums; compare: Sums | null },
  template: 'lead' | 'ecommerce',
  hasCompare: boolean,
  ct: CampaignLeaksT
) => {
  const metrics = metricsFor(template);
  const fields = ['type', 'brand'];
  metrics.forEach((metric) => {
    fields.push(metric.key);
    if (hasCompare) fields.push(`${metric.key}_compare`);
    if (metric.additive) fields.push(`${metric.key}_share`);
    if (hasCompare) fields.push(`${metric.key}_evolution`);
  });
  const line = (type: string, brand: string, current: Sums, compare: Sums | null, isTotal = false) => {
    const out: Record<string, unknown> = { type, brand };
    metrics.forEach((metric) => {
      const cur = metric.value(current);
      const cmp = compare ? metric.value(compare) : null;
      out[metric.key] = cur ?? '';
      if (hasCompare) out[`${metric.key}_compare`] = cmp ?? '';
      if (metric.additive) {
        out[`${metric.key}_share`] =
          (isTotal ? totalRowShare(metric, rows, total.current) : share(cur ?? 0, metric.value(total.current) ?? 0)) ?? '';
      }
      if (hasCompare) out[`${metric.key}_evolution`] = cur !== null && cmp ? (cur - cmp) / cmp : '';
    });
    return out;
  };
  const data = [
    ...rows.map((row) => line(row.campaign_type, brandLabel(ct, row.is_brand), row.current, row.compare)),
    line('TOTAL', '', total.current, total.compare, true),
  ];
  return { fields, data };
};
